import { API_BASE_URL, TOKEN_STORAGE_KEYS } from '../config/api';
import ApiError from './ApiError';

// Set by AuthProvider on mount so a background 401-refresh failure can clear
// the session and notify the UI without api/client.js importing React context
// (this module stays a plain, framework-agnostic fetch wrapper).
let onSessionExpired = () => {};
export const setOnSessionExpired = (fn) => {
  onSessionExpired = typeof fn === 'function' ? fn : () => {};
};

export const getStoredTokens = () => ({
  accessToken: localStorage.getItem(TOKEN_STORAGE_KEYS.access),
  refreshToken: localStorage.getItem(TOKEN_STORAGE_KEYS.refresh),
});

export const storeTokens = ({ accessToken, refreshToken }) => {
  if (accessToken) localStorage.setItem(TOKEN_STORAGE_KEYS.access, accessToken);
  if (refreshToken) localStorage.setItem(TOKEN_STORAGE_KEYS.refresh, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEYS.access);
  localStorage.removeItem(TOKEN_STORAGE_KEYS.refresh);
};

// De-duplicates concurrent refresh attempts triggered by multiple in-flight
// requests all receiving a 401 at once.
let refreshPromise = null;

const performRefresh = async () => {
  const { refreshToken } = getStoredTokens();
  if (!refreshToken) throw new ApiError({ message: 'No session to refresh', code: 'SESSION_EXPIRED' });

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ refreshToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError({
      message: body?.message || 'Session expired',
      status: response.status,
      code: 'SESSION_EXPIRED',
    });
  }
  storeTokens({ accessToken: body?.data?.accessToken, refreshToken: body?.data?.refreshToken });
  return body?.data;
};

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, value);
    });
  }
  return url.toString();
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * Central request function used by every api/endpoints/* module.
 *
 * @param {string} path - appended to API_BASE_URL, e.g. '/categories'
 * @param {object} options
 * @param {string} [options.method='GET']
 * @param {object} [options.body] - JSON-serialized if present
 * @param {object} [options.params] - query string params
 * @param {boolean} [options.auth=true] - attach Authorization header
 * @param {string} [options.idempotencyKey] - sets the Idempotency-Key header
 * @param {boolean} [options._isRetry] - internal, prevents infinite 401 retry loops
 */
export const request = async (path, options = {}) => {
  const { method = 'GET', body, params, auth = true, idempotencyKey, _isRetry = false } = options;

  const headers = { 'Content-Type': 'application/json' };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  let sentAuthHeader = false;
  if (auth) {
    const { accessToken } = getStoredTokens();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      sentAuthHeader = true;
    }
  }

  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError({ message: 'Network error — please check your connection.', code: 'NETWORK_ERROR' });
  }

  // A guest request without an Authorization header has no session to
  // refresh. When a token was rejected, refresh once; a second 401 means the
  // refreshed credentials were also rejected and the local session is stale.
  if (response.status === 401 && sentAuthHeader) {
    if (_isRetry) {
      clearTokens();
      onSessionExpired();
      throw new ApiError({ message: 'Your session has expired. Please sign in again.', code: 'SESSION_EXPIRED', status: 401 });
    }
    try {
      if (!refreshPromise) refreshPromise = performRefresh().finally(() => { refreshPromise = null; });
      await refreshPromise;
    } catch (refreshErr) {
      clearTokens();
      onSessionExpired();
      throw new ApiError({ message: 'Your session has expired. Please sign in again.', code: 'SESSION_EXPIRED', status: 401 });
    }
    return request(path, { ...options, _isRetry: true });
  }

  const data = await parseBody(response);

  if (!response.ok) {
    throw new ApiError({
      message: data?.message || `Request failed (${response.status})`,
      status: response.status,
      code: data?.code || null,
      details: data,
    });
  }

  return data;
};

const apiClient = { request, setOnSessionExpired, getStoredTokens, storeTokens, clearTokens };
export default apiClient;
